import * as React from 'react';
import { Root } from 'react-dom/client';
import {
  Constructor,
  GeneralPath,
  IGroupBoundsCalculator,
  INode,
  IGroupPaddingProvider,
  INodeSizeConstraintProvider,
  IRenderContext,
  NodeStyleBase,
  Rect,
  SvgVisual,
  Visual,
} from '@ardoq/yfiles';
import {
  AGGREGATED_COUNT_BADGE_RADIUS,
  CollapsibleGraphGroup,
  disposeCallback,
  ensureRoot,
  estimateCountLabelWidth,
  initializeRoot,
} from '@ardoq/graph';
import { truncateText } from '@ardoq/graph-labeling';
import { logError } from '@ardoq/logging';
import { CollapsibleGroup } from '../CollapsibleGroup';
import { GroupInsetsProvider } from './GroupInsetsProvider';
import {
  GroupBoundsCalculator,
  getGroupLabelDimensions,
} from './GroupBoundsCalculator';
import SparkMD5 from 'spark-md5';
import { createSvgElement } from '@ardoq/dom-utils';
import { getChangedPopover } from 'scope/modelUtil';
import { classes, getCssClassFromDiffType } from '@ardoq/common-helpers';
import { DiffType } from '@ardoq/data-model';
import {
  isContextNode,
  isPartOfMultiSelection,
} from 'yfilesExtensions/styles/nodeDecorator';
import { ComponentGroup } from '../ComponentGroup';
import {
  CONTEXT_HIGHLIGHT_PADDING,
  DATA_RENDER_HASH,
} from 'yfilesExtensions/styles/consts';
import { GroupType, ViewIds } from '@ardoq/api-types';
import { CSS_CLASS_IGNORE_LINK_TARGET } from '@ardoq/component-hierarchies';
import { colors } from '@ardoq/design-tokens';
import { getNodeOutline } from 'yfilesExtensions/getNodeOutline';
import { getNodeBounds } from 'yfilesExtensions/getNodeBounds';
import { GLOBAL_HANDLER_ID_ATTRIBUTE, MODEL_ID_ATTRIBUTE } from 'consts';
import { selectComponent } from 'streams/components/ComponentActions';
import { dispatchAction } from '@ardoq/rxbeach';
import { EXPANDER_RADIUS } from '../consts';
import {
  folderStyleGroupLabelStartX,
  getGroupHeaderWidthWithoutLabel,
  measureGroupSubLabel,
} from '../utils';
import { BULK_POPOVER_ATTR } from '@ardoq/popovers';

const truncateGroupSubLabel = truncateText(measureGroupSubLabel);

const createRenderDataCache = (node: INode, group: CollapsibleGraphGroup) =>
  SparkMD5.hash(
    `${group.getShape()},${node.layout.width},${node.layout.height},${
      group.collapsed ? group.descendantCount : -1
    },${group.getLabel()},${group.subLabel},${isContextNode(
      node
    )},${group.getCSS({
      useAsBackgroundStyle: true,
    })},${group.getVisualDiffType()},${group.isTransparentized}`
  );

const createElementAsGroupFolder = (
  node: INode,
  group: CollapsibleGraphGroup,
  viewId: ViewIds,
  viewInstanceId: string
) => {
  const isRootGroup = !group.parent;
  const hasBorder = !isRootGroup;
  const isComponent = group.isComponent();
  const isWorkspace = group.type === GroupType.WORKSPACE;
  const borderColor = hasBorder && !isComponent ? colors.grey400 : undefined;
  const visualDiffType = group.getVisualDiffType();
  const visualDiffClass = getCssClassFromDiffType(visualDiffType);

  const { groupLabelHeight, legacyFormattingHeight, subLabelHeight } =
    getGroupLabelDimensions(node);

  const subLabelIndent = getGroupHeaderWidthWithoutLabel(group);

  const groupSubLabelTruncated = truncateGroupSubLabel(
    group.subLabel || '',
    node.layout.width - subLabelIndent
  );

  const subLabelOffsetX = folderStyleGroupLabelStartX(group);

  const subLabelOffestY =
    groupLabelHeight + legacyFormattingHeight + subLabelHeight;

  return React.createElement(CollapsibleGroup, {
    viewId,
    viewInstanceId,
    group,
    borderColor,
    className: isComponent
      ? classes(
          group.getCSS({
            useAsBackgroundStyle: !visualDiffClass, // due to poor design in ardoqModelCSSManager and the liberal sprinkling of css !important, we cannot use the background style when visualDiffClass is present.
          }),
          CSS_CLASS_IGNORE_LINK_TARGET
        )
      : isWorkspace
        ? 'workspace'
        : undefined,
    height: node.layout.height,
    width: node.layout.width,
    subLabelOffestY,
    offsetX: subLabelOffsetX,
    subTitle: groupSubLabelTruncated,
    descendantCount: group.descendantCount,
    visualDiffClass,
  });
};

const createElementAsCollapsedComponent = (
  node: INode,
  group: CollapsibleGraphGroup,
  viewId: ViewIds,
  viewInstanceId: string
) => {
  const visualDiffType = group.getVisualDiffType();
  const visualDiffClass = getCssClassFromDiffType(visualDiffType);

  const isPlaceholder = visualDiffType === DiffType.PLACEHOLDER;
  const { modelId, isContext } = group;
  const isSelectedRelated = isPartOfMultiSelection(modelId);
  const templateHref = group.getImage() ? undefined : `#${group.getShape()}`;
  const imageHref = group.getImage();
  const { width, height } = node.layout;

  return React.createElement(ComponentGroup, {
    viewId,
    viewInstanceId,
    group,
    templateHref,
    imageHref,
    isPlaceholder,
    isContext,
    isSelectedRelated,
    width,
    height,
    visualDiffClass,
    contextHighlightPadding: CONTEXT_HIGHLIGHT_PADDING,
  });
};

const render = (
  root: Root,
  container: SVGElement,
  node: INode,
  cache: string,
  viewId: ViewIds,
  viewInstanceId: string
) => {
  const { tag: group } = node;
  if (!(group instanceof CollapsibleGraphGroup)) {
    logError(Error('Non-collapsible node in CollapsibleGroupStyle render.'));
    return;
  }
  container.setAttribute(DATA_RENDER_HASH, cache);

  const isComponent = group.isComponent();

  if (group.modelId) {
    container.setAttribute(GLOBAL_HANDLER_ID_ATTRIBUTE, group.modelId);
  } else {
    container.removeAttribute(GLOBAL_HANDLER_ID_ATTRIBUTE);
  }
  container.setAttribute('data-node-id', group.id);
  container.setAttribute(
    'class',
    classes('skipContextUpdate', isComponent && 'component', 'group')
  );

  const tooltipAttributes = Object.entries(
    group.modelId
      ? getChangedPopover(group.modelId, group.getVisualDiffType())
      : {}
  );

  if (tooltipAttributes.length > 0) {
    tooltipAttributes.forEach(([key, value]) =>
      container.setAttribute(key, value)
    );
  } else {
    [BULK_POPOVER_ATTR, MODEL_ID_ATTRIBUTE].forEach(key =>
      container.removeAttribute(key)
    );
  }

  const createElement =
    isComponent && group.collapsed
      ? createElementAsCollapsedComponent
      : createElementAsGroupFolder;

  root.render(createElement(node, group, viewId, viewInstanceId));
};
export class CollapsibleGroupStyle extends NodeStyleBase {
  constructor(
    private readonly viewId: ViewIds,
    private readonly viewInstanceId: string
  ) {
    super();
  }

  createVisual(context: IRenderContext, node: INode) {
    const g = createSvgElement('g');
    g.addEventListener('dblclick', () => {
      const modelId = g.getAttribute(GLOBAL_HANDLER_ID_ATTRIBUTE);
      if (!modelId || !g.classList.contains('component')) {
        return;
      }
      dispatchAction(selectComponent({ cid: modelId }));
    });
    if (node.tag instanceof CollapsibleGraphGroup) {
      const cache = createRenderDataCache(node, node.tag);
      render(
        initializeRoot(g),
        g,
        node,
        cache,
        this.viewId,
        this.viewInstanceId
      );
    } else {
      logError(Error('Unrecognized node tag.'));
    }
    SvgVisual.setTranslate(g, node.layout.x, node.layout.y);
    const result = new SvgVisual(g);
    context.setDisposeCallback(result, disposeCallback);
    return result;
  }

  updateVisual(_context: IRenderContext, oldVisual: Visual, node: INode) {
    if (!(oldVisual instanceof SvgVisual)) {
      logError(Error('Invalid visual in CollapsibleGroupStyle updateVisual.'));
      return oldVisual;
    }
    const { tag: group, layout } = node;
    if (!(group instanceof CollapsibleGraphGroup)) {
      logError(
        Error('Non collapsible node in CollapsibleGroupStyle updateVisual.')
      );
      return oldVisual;
    }
    const container = oldVisual.svgElement;
    const oldCache = container.getAttribute(DATA_RENDER_HASH);
    const newCache = createRenderDataCache(node, group);
    if (oldCache !== newCache) {
      render(
        ensureRoot(container),
        container,
        node,
        newCache,
        this.viewId,
        this.viewInstanceId
      );
    }
    SvgVisual.setTranslate(container, layout.x, layout.y);
    return oldVisual;
  }
  protected override lookup(node: INode, type: Constructor) {
    if (type === IGroupPaddingProvider) {
      return new GroupInsetsProvider(node);
    } else if (
      type === IGroupBoundsCalculator ||
      type === INodeSizeConstraintProvider
    ) {
      return new GroupBoundsCalculator(node);
    }
    return super.lookup(node, type);
  }

  protected override getOutline(node: INode): GeneralPath | null {
    const { tag: group } = node;

    const isCollapsedComponentGroup =
      group instanceof CollapsibleGraphGroup &&
      group.collapsed &&
      group.isComponent();

    if (!isCollapsedComponentGroup) {
      return super.getOutline(node);
    }

    const { x, y, width, height } = getNodeBounds(
      node,
      isContextNode(node),
      true
    );
    const nodeCenterY = y + height / 2;

    // #region expander rect
    const expanderX = x - EXPANDER_RADIUS;
    const expanderY = nodeCenterY - EXPANDER_RADIUS;
    const expanderDiameter = 2 * EXPANDER_RADIUS;

    const expanderRect = new Rect(
      expanderX,
      expanderY,
      expanderDiameter,
      expanderDiameter
    );
    // #endregion

    // #region descendant count badge rect
    const descendantCountBadgeHeight = 2 * AGGREGATED_COUNT_BADGE_RADIUS;
    const descendantCountBadgeWidth = Math.max(
      estimateCountLabelWidth(group.descendantCount),
      descendantCountBadgeHeight
    );
    const descendantCountBadgeX = x + width - descendantCountBadgeWidth / 2;
    const descendantCountBadgeY = y - descendantCountBadgeHeight / 2;
    const descendantCountBadgeRect = new Rect(
      descendantCountBadgeX,
      descendantCountBadgeY,
      descendantCountBadgeWidth,
      descendantCountBadgeHeight
    );
    // #endregion

    const outline = getNodeOutline(node);
    outline.appendEllipse(expanderRect, false);
    outline.appendRectangle(descendantCountBadgeRect, false);

    return outline;
  }
}
